import React, { useEffect, useMemo, useState } from "react";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { rankWith, schemaMatches } from "@jsonforms/core";
import { useTranslation } from "react-i18next";
import {
  SFDrawer,
  SFButton,
  SFDataGridClient,
  getStyled,
  SFStack,
} from "@sf-core";
import { RadioButtonRenderer } from "./radioButtonRenderer";
import { getBackgrounds } from "../../../services/assetUploadService";

const { styled } = getStyled();

const SFDatagridStyled = styled(SFDataGridClient)(({ theme }) => ({
  "& .ag-header": { display: "none" },
  "& .ag-status-bar": { display: "none" },
  "& .ag-root-wrapper": { borderTop: "none", marginTop: 0, paddingTop: 0 },
  "& .ag-center-cols-container .ag-row-hover": {
    "&:before": { backgroundColor: theme.palette.blue50.main },
    borderBottom: `1px solid ${theme.palette.blue100.main}`,
  },
  "& .ag-row-group-leaf-indent": { marginLeft: theme.spacing(3) },
  "& .ag-pinned-left-cols-container .ag-cell": { paddingLeft: 0 },
}));

const ButtonStack = styled(SFStack)(({ theme }) => ({
  marginLeft: theme.spacing(3),
  height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const SFButtonStyled = styled(SFButton)(({ theme }) => ({
  width: "max-content",
  height: "max-content",
}));

const OpenDrawerRenderer = ({ uischema, data, handleChange }) => {
  const { t } = useTranslation();
  const { options } = uischema;

  const [showVideoList, setShowVideoList] = useState(false);
  const [videoData, setVideoData] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(data?.background || "");

  useEffect(() => {
    getBackgrounds().then((res) => {
      //process data before passing to grid
      const transformedBackgrounds = res.data.backgrounds.map((filePath, index) => {
        const fileName = filePath.split("/").pop();
        return {
          id: index + 1,
          name: fileName
        };
      });
      setVideoData(transformedBackgrounds);
    });


  }, []);

  const closeVideoListDrawer = () => setShowVideoList(false);
  const handleOpenDrawer = () => setShowVideoList(true);

  const handleSelectedVideo = (selected) => {
    setSelectedVideo(selected.name);
  };

  const saveSelectedVideo = () => {
    handleChange("background", selectedVideo); // JSON Forms expects (path, value)
    setShowVideoList(false);
  };

  const colDefs = useMemo(
    () => [
      { headerName: "", field: "id", hide: true, suppressFiltersToolPanel: true },
      {
        suppressFiltersToolPanel: true,
        type: "leftAligned",
        cellRenderer: "RadioButtonRenderer",
        cellRendererParams: {
          selectedVideoName: selectedVideo,
          onSelect: handleSelectedVideo,
        },
        suppressSizeToFit: true,
        width: 50,
      },
      { headerName: "", field: "name", suppressFiltersToolPanel: true, flex: 1 },
    ],
    [selectedVideo]
  );

  return (
    <>
      <SFDrawer
        disablePaddingTop
        size="m"
        open={showVideoList}
        onClose={closeVideoListDrawer}
        header={t("energizeBuilder.splash.drawerHeader")}
        footer={
          <SFStack spacing={2} direction="row" alignItems="center" justifyContent="start">
            <SFButton variant="secondary" onClick={closeVideoListDrawer}>
              {t("energizeBuilder.splash.cancel")}
            </SFButton>
            <SFButton variant="primary" onClick={saveSelectedVideo}>
              {t("energizeBuilder.splash.save")}
            </SFButton>
          </SFStack>
        }
      >
        <SFDatagridStyled
          colDefs={colDefs}
          items={videoData}
          persistState={false}
          customComponents={{
            RadioButtonRenderer,
          }}
        />
      </SFDrawer>

      <ButtonStack>
        <SFButtonStyled
          variant={options.variant}
          onClick={handleOpenDrawer}
          disabled={options.readOnly}
        >
          {t("components.formBuilder.buttonLabels.replace")}
        </SFButtonStyled>
      </ButtonStack>
    </>
  );
};

export const OpenDrawerRendererTester = rankWith(
  106,
  schemaMatches((schema) => schema.openDrawer === true)
);

export default withJsonFormsControlProps(OpenDrawerRenderer);
